/**
 * Publisher-style visual effects for shapes, pictures and text boxes.
 *
 * Object-level effects (object.properties):
 *   shadow:     { preset, dx, dy, blur, color, opacity, inner?: bool, cast?: 'ul'|'ur'|'below'|'ll'|'lr' }  (legacy {dx,dy,blur,color} still works)
 *   reflection: { preset, visible: 0..1 (fraction of height mirrored), offset: px, opacity }
 *   glow:       { preset, size: px, color: hex, opacity }
 *   softEdges:  { radius: px }
 *   bevel:      { preset, width: multiplier, depth: multiplier }
 *   rotation3d: { preset, rx, ry, rz, persp: 0 (parallel) | multiplier of object size }
 *
 * Text (glyph) effects live in properties.textEffects: { shadow, reflection, glow, bevel } and map to CSS text-shadow / box-reflect.
 */
import React from 'react';
import { hexToRgb } from './colors';

export const FRAME_EFFECT_KEYS = ['shadow', 'reflection', 'glow', 'softEdges', 'bevel', 'rotation3d'];

const rgba = (color, opacity) => {
  if (!color) return `rgba(0,0,0,${opacity ?? 0.4})`;
  if (color.startsWith('rgba') || color.startsWith('rgb')) return color;
  try {
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r},${g},${b},${opacity ?? 1})`;
  } catch {
    return color;
  }
};

/* ------------------------------------------------------------------ */
/* Presets                                                             */
/* ------------------------------------------------------------------ */

const OFFSETS = [
  ['br', 'Bottom Right', 1, 1], ['b', 'Bottom', 0, 1], ['bl', 'Bottom Left', -1, 1],
  ['r', 'Right', 1, 0], ['c', 'Center', 0, 0], ['l', 'Left', -1, 0],
  ['tr', 'Top Right', 1, -1], ['t', 'Top', 0, -1], ['tl', 'Top Left', -1, -1],
];

export const SHADOW_PRESETS = {
  outer: OFFSETS.map(([k, name, sx, sy]) => ({
    id: `outer-${k}`, name: `Offset: ${name}`, group: 'Outer',
    value: { dx: sx * 4, dy: sy * 4, blur: k === 'c' ? 10 : 6, color: '#000000', opacity: k === 'c' ? 0.45 : 0.4 },
  })),
  inner: OFFSETS.map(([k, name, sx, sy]) => ({
    id: `inner-${k}`, name: `Inside: ${name}`, group: 'Inner',
    // The shadow falls on the named inner edges, so the mask is offset the opposite way.
    value: { inner: true, dx: -sx * 4, dy: -sy * 4, blur: k === 'c' ? 10 : 6, color: '#000000', opacity: 0.5 },
  })),
  perspective: [
    // [id, name, transform (applied right-to-left), origin]. Casts are silhouette copies of the object.
    ['ul', 'Perspective: Upper Left', 'skewX(30deg) scaleY(-0.55)', 'top center'],
    ['ur', 'Perspective: Upper Right', 'skewX(-30deg) scaleY(-0.55)', 'top center'],
    ['below', 'Perspective: Below', 'translateY(100%) scaleY(0.35)', 'top center'],
    ['ll', 'Perspective: Lower Left', 'translateY(100%) skewX(-30deg) scaleY(0.6)', 'top center'],
    ['lr', 'Perspective: Lower Right', 'translateY(100%) skewX(30deg) scaleY(0.6)', 'top center'],
  ].map(([k, name, transform, origin]) => ({
    id: `persp-${k}`, name, group: 'Perspective',
    value: { cast: k, dx: 0, dy: 0, blur: 8, color: '#000000', opacity: 0.35, castTransform: transform, castOrigin: origin },
  })),
};

export const GLOW_SIZES = [5, 8, 11, 18];
export const glowPreset = (size, color) => ({ preset: `glow-${size}`, size, color, opacity: 0.6 });

export const REFLECTION_PRESETS = [
  ['tight', 'Tight', 0.3], ['half', 'Half', 0.55], ['full', 'Full', 1],
].flatMap(([k, name, visible]) => [0, 4, 8].map((offset) => ({
  id: `${k}-${offset}`, name: `${name} Reflection: ${offset === 0 ? 'Touching' : `${offset} pt offset`}`,
  value: { preset: `${k}-${offset}`, visible, offset, opacity: 0.5 },
})));

export const SOFT_EDGE_SIZES = [1, 2.5, 5, 10, 25, 50];

export const BEVEL_PRESETS = [
  { id: 'circle', name: 'Circle', soft: 4, depth: 6, spec: 0.6, shine: 20 },
  { id: 'relaxed', name: 'Relaxed Inset', soft: 6, depth: 3, spec: 0.3, shine: 10 },
  { id: 'cross', name: 'Cross', soft: 2.5, depth: 8, spec: 0.8, shine: 30 },
  { id: 'angle', name: 'Angle', soft: 3, depth: 10, spec: 0.5, shine: 8 },
  { id: 'soft-round', name: 'Soft Round', soft: 8, depth: 4, spec: 0.4, shine: 25 },
  { id: 'convex', name: 'Convex', soft: 10, depth: 8, spec: 0.7, shine: 15 },
  { id: 'slope', name: 'Slope', soft: 5, depth: 12, spec: 0.3, shine: 5 },
  { id: 'divot', name: 'Divot', soft: 3, depth: -8, spec: 0.6, shine: 20 },
  { id: 'riblet', name: 'Riblet', soft: 1.5, depth: 10, spec: 0.9, shine: 40 },
  { id: 'hard-edge', name: 'Hard Edge', soft: 1, depth: 14, spec: 1, shine: 60 },
  { id: 'art-deco', name: 'Art Deco', soft: 2, depth: 6, spec: 0.8, shine: 45, elevation: 30 },
  { id: 'cool-slant', name: 'Cool Slant', soft: 7, depth: 5, spec: 0.5, shine: 12, azimuth: 315 },
];

export const ROTATION3D_PRESETS = {
  parallel: [
    { id: 'iso-left-down', name: 'Isometric: Left Down', rx: 30, ry: 40, rz: 0, persp: 0 },
    { id: 'iso-right-up', name: 'Isometric: Right Up', rx: 30, ry: -40, rz: 0, persp: 0 },
    { id: 'iso-top-up', name: 'Isometric: Top Up', rx: -30, ry: 40, rz: 0, persp: 0 },
    { id: 'iso-bottom-down', name: 'Isometric: Bottom Down', rx: -30, ry: -40, rz: 0, persp: 0 },
    { id: 'off1-left', name: 'Off Axis 1: Left', rx: 12, ry: 50, rz: 0, persp: 0 },
    { id: 'off1-right', name: 'Off Axis 1: Right', rx: 12, ry: -50, rz: 0, persp: 0 },
    { id: 'off1-top', name: 'Off Axis 1: Top', rx: 55, ry: 0, rz: 0, persp: 0 },
    { id: 'off2-left', name: 'Off Axis 2: Left', rx: -12, ry: 50, rz: 0, persp: 0 },
    { id: 'off2-right', name: 'Off Axis 2: Right', rx: -12, ry: -50, rz: 0, persp: 0 },
    { id: 'off2-top', name: 'Off Axis 2: Top', rx: -55, ry: 0, rz: 0, persp: 0 },
  ],
  perspective: [
    { id: 'persp-front', name: 'Perspective: Front', rx: 0, ry: 0, rz: 0, persp: 2.5 },
    { id: 'persp-above', name: 'Perspective: Above', rx: 22, ry: 0, rz: 0, persp: 2.2 },
    { id: 'persp-below', name: 'Perspective: Below', rx: -22, ry: 0, rz: 0, persp: 2.2 },
    { id: 'persp-left', name: 'Perspective: Left', rx: 0, ry: 26, rz: 0, persp: 2.2 },
    { id: 'persp-right', name: 'Perspective: Right', rx: 0, ry: -26, rz: 0, persp: 2.2 },
    { id: 'persp-relaxed-mod', name: 'Perspective: Relaxed Moderately', rx: 24, ry: 0, rz: 0, persp: 1.6 },
    { id: 'persp-relaxed', name: 'Perspective: Relaxed', rx: 40, ry: 0, rz: 0, persp: 1.4 },
    { id: 'persp-contrast-left', name: 'Perspective: Contrasting Left', rx: 18, ry: -32, rz: 0, persp: 1.5 },
    { id: 'persp-contrast-right', name: 'Perspective: Contrasting Right', rx: 18, ry: 32, rz: 0, persp: 1.5 },
    { id: 'persp-heroic-left', name: 'Perspective: Heroic Extreme Left', rx: 12, ry: 52, rz: 0, persp: 1.1 },
    { id: 'persp-heroic-right', name: 'Perspective: Heroic Extreme Right', rx: 12, ry: -52, rz: 0, persp: 1.1 },
  ],
};

/* ------------------------------------------------------------------ */
/* Style builders                                                      */
/* ------------------------------------------------------------------ */

export const hasFrameEffects = (p) => !!p && FRAME_EFFECT_KEYS.some((k) => p[k]);

const softEdgeFilter = (id, radius) => (
  <filter key="soft" id={id} x="-25%" y="-25%" width="150%" height="150%" colorInterpolationFilters="sRGB">
    <feMorphology in="SourceAlpha" operator="erode" radius={Math.max(0.5, radius / 2)} result="er" />
    <feGaussianBlur in="er" stdDeviation={Math.max(0.5, radius / 2)} result="bl" />
    <feComposite in="SourceGraphic" in2="bl" operator="in" />
  </filter>
);

const innerShadowFilter = (id, s) => (
  <filter key="inner" id={id} x="-25%" y="-25%" width="150%" height="150%" colorInterpolationFilters="sRGB">
    <feComponentTransfer in="SourceAlpha" result="inv"><feFuncA type="table" tableValues="1 0" /></feComponentTransfer>
    <feGaussianBlur in="inv" stdDeviation={Math.max(0.1, s.blur / 2)} result="blur" />
    <feOffset in="blur" dx={s.dx} dy={s.dy} result="off" />
    <feFlood floodColor={s.color || '#000000'} floodOpacity={s.opacity ?? 0.5} result="flood" />
    <feComposite in="flood" in2="off" operator="in" result="sh" />
    <feComposite in="sh" in2="SourceAlpha" operator="in" result="inner" />
    <feMerge><feMergeNode in="SourceGraphic" /><feMergeNode in="inner" /></feMerge>
  </filter>
);

const bevelFilter = (id, b) => {
  const preset = BEVEL_PRESETS.find((x) => x.id === b.preset) || BEVEL_PRESETS[0];
  const soft = Math.max(0.3, preset.soft * (b.width || 1));
  const depth = preset.depth * (b.depth || 1);
  const elev = preset.elevation || 50;
  const az = preset.azimuth || 225;
  const diffuse = (1 / Math.sin((elev * Math.PI) / 180)).toFixed(3);
  return (
    <filter key="bevel" id={id} x="-25%" y="-25%" width="150%" height="150%" colorInterpolationFilters="sRGB">
      <feGaussianBlur in="SourceAlpha" stdDeviation={soft} result="blur" />
      <feDiffuseLighting in="blur" surfaceScale={depth} diffuseConstant={diffuse} lightingColor="#ffffff" result="diff"><feDistantLight azimuth={az} elevation={elev} /></feDiffuseLighting>
      <feComposite in="SourceGraphic" in2="diff" operator="arithmetic" k1="1" k2="0" k3="0" k4="0" result="shaded" />
      <feSpecularLighting in="blur" surfaceScale={depth} specularConstant={preset.spec} specularExponent={preset.shine} lightingColor="#ffffff" result="spec"><feDistantLight azimuth={az} elevation={elev} /></feSpecularLighting>
      <feComposite in="spec" in2="SourceAlpha" operator="in" result="spec2" />
      <feComposite in="shaded" in2="spec2" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="lit" />
      <feComposite in="lit" in2="SourceAlpha" operator="in" />
    </filter>
  );
};

/**
 * Build everything the EffectsFrame needs.
 * Returns { filter, defs, transform, reflection, cast } — all null/empty when there are no effects.
 */
export const buildFrameEffects = (p, scale = 1, uid = 'fx', box = { width: 100, height: 100 }) => {
  const out = { filter: undefined, defs: [], transform: undefined, reflection: null, cast: null };
  if (!hasFrameEffects(p)) return out;
  const parts = [];

  if (p.bevel) {
    const id = `${uid}-bevel`;
    // Scale both the ramp width and its height so the bevel's slope (and therefore its lighting) is zoom-invariant.
    out.defs.push(bevelFilter(id, { ...p.bevel, width: (p.bevel.width || 1) * scale, depth: (p.bevel.depth || 1) * scale }));
    parts.push(`url(#${id})`);
  }
  if (p.softEdges?.radius) {
    const id = `${uid}-soft`;
    out.defs.push(softEdgeFilter(id, p.softEdges.radius * scale));
    parts.push(`url(#${id})`);
  }
  if (p.shadow?.inner) {
    const id = `${uid}-inner`;
    out.defs.push(innerShadowFilter(id, { ...p.shadow, dx: (p.shadow.dx || 0) * scale, dy: (p.shadow.dy || 0) * scale, blur: (p.shadow.blur || 6) * scale }));
    parts.push(`url(#${id})`);
  }
  if (p.glow?.size) {
    const c = rgba(p.glow.color || '#7c3aed', p.glow.opacity ?? 0.6);
    const s = p.glow.size * scale;
    parts.push(`drop-shadow(0 0 ${s}px ${c})`, `drop-shadow(0 0 ${s * 0.6}px ${c})`);
  }
  if (p.shadow && !p.shadow.inner && !p.shadow.cast) {
    const c = p.shadow.color?.startsWith('rgba') ? p.shadow.color : rgba(p.shadow.color || '#000000', p.shadow.opacity ?? 0.4);
    parts.push(`drop-shadow(${(p.shadow.dx ?? 3) * scale}px ${(p.shadow.dy ?? 3) * scale}px ${(p.shadow.blur ?? 6) * scale}px ${c})`);
  }
  if (p.shadow?.cast) {
    out.cast = { transform: p.shadow.castTransform, origin: p.shadow.castOrigin, blur: (p.shadow.blur ?? 8) * scale, opacity: p.shadow.opacity ?? 0.35 };
  }
  if (p.reflection) {
    out.reflection = { visible: p.reflection.visible ?? 0.5, offset: (p.reflection.offset || 0) * scale, opacity: p.reflection.opacity ?? 0.5 };
  }
  if (p.rotation3d) {
    const r = p.rotation3d;
    const size = Math.max(box.width, box.height) * scale;
    const persp = r.persp ? `perspective(${Math.max(120, size * r.persp)}px) ` : '';
    out.transform = `${persp}rotateX(${r.rx || 0}deg) rotateY(${r.ry || 0}deg) rotateZ(${r.rz || 0}deg)`;
  }
  out.filter = parts.length ? parts.join(' ') : undefined;
  return out;
};

/* ------------------------------------------------------------------ */
/* Text (glyph) effects                                                */
/* ------------------------------------------------------------------ */

export const TEXT_SHADOW_PRESETS = {
  outer: OFFSETS.map(([k, name, sx, sy]) => ({ id: `outer-${k}`, name: `Offset: ${name}`, value: { preset: `outer-${k}`, dx: sx * 2, dy: sy * 2, blur: k === 'c' ? 4 : 3, color: 'rgba(0,0,0,0.55)' } })),
  perspective: [
    ['ul', 'Upper Left', -4, -3], ['ur', 'Upper Right', 4, -3], ['below', 'Below', 0, 5], ['ll', 'Lower Left', -4, 4], ['lr', 'Lower Right', 4, 4],
  ].map(([k, name, dx, dy]) => ({ id: `persp-${k}`, name: `Perspective: ${name}`, value: { preset: `persp-${k}`, dx, dy, blur: 5, color: 'rgba(0,0,0,0.4)' } })),
};

export const TEXT_BEVEL_PRESETS = BEVEL_PRESETS.map((b) => ({ id: b.id, name: b.name, value: { preset: b.id, hl: Math.max(0.6, b.soft / 4), sh: Math.max(0.6, b.soft / 4), strength: Math.min(1, Math.abs(b.depth) / 10) } }));

/** CSS text-shadow list for glyph effects. */
export const textEffectShadows = (effects = {}, scale = 1) => {
  const shadows = [];
  if (effects.shadow) shadows.push(`${(effects.shadow.dx ?? 2) * scale}px ${(effects.shadow.dy ?? 2) * scale}px ${(effects.shadow.blur ?? 3) * scale}px ${effects.shadow.color || 'rgba(0,0,0,0.5)'}`);
  if (effects.glow) {
    const c = rgba(effects.glow.color || '#7c3aed', effects.glow.opacity ?? 0.7);
    const s = (effects.glow.size ?? 6) * scale;
    shadows.push(`0 0 ${s}px ${c}`, `0 0 ${s * 2}px ${c}`);
  }
  if (effects.bevel) {
    const b = effects.bevel;
    const hl = (b.hl ?? 1) * scale;
    const sh = (b.sh ?? 1) * scale;
    const k = b.strength ?? 0.6;
    const invert = b.preset === 'divot';
    shadows.push(`${invert ? -hl : hl}px ${invert ? -hl : hl}px 0 rgba(255,255,255,${0.35 + 0.4 * k})`, `${invert ? sh : -sh}px ${invert ? sh : -sh}px ${b.preset === 'soft-round' || b.preset === 'relaxed' ? sh : 0}px rgba(0,0,0,${0.2 + 0.3 * k})`);
  }
  return shadows;
};

export const textReflectionCss = (reflection, scale = 1) => {
  if (!reflection) return undefined;
  const visible = reflection.visible ?? 0.45;
  const start = Math.round((1 - visible) * 100);
  return `below ${(reflection.offset ?? 2) * scale}px linear-gradient(transparent ${start}%, rgba(0,0,0,${reflection.opacity ?? 0.35}))`;
};
